import { useEffect, useRef, useState, type JSX } from 'react';
import { Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Filter4Icon from '@mui/icons-material/Filter4';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import GroupsIcon from '@mui/icons-material/Groups';
import ReceiptIcon from '@mui/icons-material/Receipt';
import UpdateIcon from '@mui/icons-material/Update';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import { NodeTypes } from '@utils/flows';

import s from './FlowEditNavBar.module.css';

const NavBarButtonIconMap: Record<string, JSX.Element> = {
  Filter4Icon: <Filter4Icon />,
  DisplaySettingsIcon: <DisplaySettingsIcon />,
  CallSplitIcon: <CallSplitIcon />,
  GroupsIcon: <GroupsIcon />,
  ReceiptIcon: <ReceiptIcon />,
  UpdateIcon: <UpdateIcon />,
  CancelIcon: <CancelIcon />,
  CheckCircleIcon: <CheckCircleIcon />,
  AccountBalanceIcon: <AccountBalanceIcon />,
};

type NavBarButtonProps = {
  title: string;
  iconName: string;
  nodeType: string;
  isDisabled?: boolean;
  onClick: (nodeType: string) => void;
};

const NavBarButton = ({ title, iconName, nodeType, isDisabled, onClick }: NavBarButtonProps) => {
  return (
    <button
      type="button"
      disabled={isDisabled}
      className={`${s.navBarButton} ${isDisabled && s.disabledNavButton}`}
      onClick={() => onClick(nodeType)}>
      {NavBarButtonIconMap[iconName]}
      <span className={s.navBarButtonText}>{title}</span>
    </button>
  );
};

type NavBarButtonType = {
  title: string;
  iconName: string;
  nodeType: string;
  isDisabled?: boolean;
};

const NavBarButtonListStartNodes: NavBarButtonType[] = [
  {
    title: 'Settings Node',
    iconName: 'DisplaySettingsIcon',
    nodeType: NodeTypes.settingsNode,
  },
  { title: 'Error Node', iconName: 'Filter4Icon', nodeType: NodeTypes.errorNode },
];
const NavBarButtonListConditionals: NavBarButtonType[] = [
  {
    title: 'Conditional Node',
    iconName: 'CallSplitIcon',
    nodeType: NodeTypes.conditionalNode,
  },
  { title: 'Test Groups Node', iconName: 'GroupsIcon', nodeType: NodeTypes.testGroupsNode },
  {
    title: 'Transaction Nature Node',
    iconName: 'ReceiptIcon',
    nodeType: NodeTypes.transactionNatureNode,
  },
  {
    title: 'Debt Collection Node',
    iconName: 'AccountBalanceIcon',
    nodeType: NodeTypes.debtCollectionNode,
  },
];
const NavBarButtonListActions: NavBarButtonType[] = [
  {
    title: 'Recurring Action Node',
    iconName: 'UpdateIcon',
    nodeType: NodeTypes.recurringActionNode,
  },
  {
    title: 'Settings Value Node',
    iconName: 'ReceiptIcon',
    nodeType: NodeTypes.valueNode,
  },
  {
    title: 'Failure Node',
    iconName: 'CancelIcon',
    nodeType: NodeTypes.failureNode,
  },
  {
    title: 'Success Node',
    iconName: 'CheckCircleIcon',
    nodeType: NodeTypes.successNode,
  },
];

type NavBarProps = {
  isOpen: boolean;
  parentRef: React.RefObject<HTMLDivElement | null>;
  handleNavBarToggle: () => void;
  handleNewNode: (nodeType: string) => void;
};

const NavBar = ({ isOpen, parentRef, handleNavBarToggle, handleNewNode }: NavBarProps) => {
  const navBarRef = useRef<HTMLDivElement>(null);

  // Event listener for clicks outside the navbar
  useEffect(() => {
    const handleClickEventOutsde = (event: MouseEvent) => {
      if (isOpen && parentRef.current && !parentRef.current.contains(event.target as Node)) {
        handleNavBarToggle();
      }
    };

    window.addEventListener('click', handleClickEventOutsde);

    return () => {
      window.removeEventListener('click', handleClickEventOutsde);
    };
  }, [isOpen, parentRef, handleNavBarToggle]);

  // Event listener for keydown
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleNavBarToggle();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, handleNavBarToggle]);

  if (!isOpen) {
    return null;
  }

  const handleComponentClick = (nodeType: string) => {
    handleNewNode(nodeType);
  };

  return (
    <div ref={navBarRef} className={s.navBar} onClick={(e) => e.stopPropagation()}>
      <p className={s.navBarTitle}>Start Nodes:</p>
      <Divider />
      <div className={s.navBarButtonList}>
        {NavBarButtonListStartNodes.map((button) => (
          <NavBarButton
            key={button.title}
            title={button.title}
            iconName={button.iconName}
            nodeType={button.nodeType}
            isDisabled={button.isDisabled}
            onClick={handleComponentClick}
          />
        ))}
      </div>
      <p className={s.navBarTitle}>Conditional Nodes:</p>
      <Divider />
      <div className={s.navBarButtonList}>
        {NavBarButtonListConditionals.map((button) => (
          <NavBarButton
            key={button.title}
            title={button.title}
            iconName={button.iconName}
            nodeType={button.nodeType}
            isDisabled={button.isDisabled}
            onClick={handleComponentClick}
          />
        ))}
      </div>
      <p className={s.navBarTitle}>Action Nodes:</p>
      <Divider />
      <div className={s.navBarButtonList}>
        {NavBarButtonListActions.map((button) => (
          <NavBarButton
            key={button.title}
            title={button.title}
            iconName={button.iconName}
            nodeType={button.nodeType}
            isDisabled={button.isDisabled}
            onClick={handleComponentClick}
          />
        ))}
      </div>
    </div>
  );
};

type FlowEditNavBarProps = {
  handleNewNode: (nodeType: string) => void;
};

function FlowEditNavBar({ handleNewNode }: FlowEditNavBarProps) {
  const [isNavBarOpen, setIsNavBarOpen] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  const handleNavBarToggle = () => {
    setIsNavBarOpen(!isNavBarOpen);
  };

  return (
    <div className={s.navBarContainer} ref={parentRef}>
      <button type="button" className={s.toggleButton} onClick={handleNavBarToggle}>
        <MenuIcon />
      </button>
      <NavBar
        isOpen={isNavBarOpen}
        parentRef={parentRef}
        handleNavBarToggle={handleNavBarToggle}
        handleNewNode={handleNewNode}
      />
    </div>
  );
}

export default FlowEditNavBar;
